import React, { useState } from 'react';
import { Quotation, FlightInfo } from './types';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult
} from '@hello-pangea/dnd';
import { v4 as uuidv4 } from 'uuid';
import { 
  PlaneTakeoff,
  GripVertical, 
  PlusCircle, 
  X,
  MoreHorizontal,
  ArrowUp,
  ArrowDown,
  Pencil,
  Trash
} from 'lucide-react';
import { Switch } from "@/components/ui/switch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { FlightGroup } from './types';

// Extended Quotation interface
export interface ExtendedQuotation extends Quotation {
  flightGroups?: FlightGroup[];
  useCustomFlightGroups?: boolean;
}

// Add an extended version of FlightInfo with a page property
interface ExtendedFlightInfo extends FlightInfo {
  page?: number;
  pageId?: string;
  pageTitle?: string;
}

interface FlightsTabProps {
  quotation: ExtendedQuotation;
  handleChange: (field: keyof ExtendedQuotation, value: any) => void;
  isEditing?: boolean;
}

export function QuotationFlightsTab({ quotation, handleChange, isEditing = false }: FlightsTabProps) {
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDescription, setNewGroupDescription] = useState('');
  
  const useCustomGroups = quotation.useCustomFlightGroups || false;
  const groups = quotation.flightGroups || [];
  
  // Get all flights from all pages
  const allFlights = quotation.pages.flatMap(page => 
    (page.flights || []).map(flight => ({
      ...flight,
      page: page.globalOrder || Number(page.id.replace(/\D/g, '')) || 1,
      pageId: page.id,
      pageTitle: page.title || `Página ${page.id}`
    })) as ExtendedFlightInfo[]
  );

  // Handler for toggling custom groups
  const handleToggleCustomGroups = (checked: boolean) => {
    handleChange('useCustomFlightGroups', checked);
    
    // If enabling custom groups for the first time, initialize with default groups by page
    if (checked && (!quotation.flightGroups || quotation.flightGroups.length === 0)) {
      // Group flights by page number
      const pageGroups: Record<string, string[]> = {}; // Use string keys for safety
      
      // Make sure we're working with flights that have page property
      allFlights.forEach((flight: ExtendedFlightInfo) => {
        const pageId = flight.pageId || '1';
        if (!pageGroups[pageId]) {
          pageGroups[pageId] = [];
        }
        pageGroups[pageId].push(flight.id);
      });
      
      // Create initial groups based on pages
      const initialGroups: FlightGroup[] = Object.entries(pageGroups).map(([pageId, ids], index) => {
        // Find the page title for this pageId
        const page = quotation.pages.find(p => p.id === pageId);
        return {
          id: uuidv4(),
          name: page?.title || `Página ${pageId}`,
          order: index,
          flightIds: ids
        };
      });
      
      handleChange('flightGroups', initialGroups);
    }
  };

  // Handler for adding a new group
  const handleAddGroup = () => {
    const newGroups = [...groups, {
      id: uuidv4(),
      name: `Novo Grupo ${groups.length + 1}`,
      order: groups.length,
      flightIds: []
    }];
    
    handleChange('flightGroups', newGroups);
  };

  // Handler for editing group name
  const handleEditGroupName = (groupId: string, currentName: string, currentDescription?: string) => {
    setEditingGroupId(groupId);
    setNewGroupName(currentName);
    setNewGroupDescription(currentDescription || '');
  };

  // Handler for saving edited group name
  const handleSaveGroupName = (groupId: string) => {
    const updatedGroups = groups.map(g => 
      g.id === groupId ? { ...g, name: newGroupName, description: newGroupDescription } : g
    );
    
    handleChange('flightGroups', updatedGroups);
    setEditingGroupId(null);
    setNewGroupName('');
    setNewGroupDescription('');
  };

  // Handler for removing a group
  const handleRemoveGroup = (groupId: string) => {
    const updatedGroups = groups
      .filter(g => g.id !== groupId)
      .map((g, index) => ({ ...g, order: index }));
    
    handleChange('flightGroups', updatedGroups);
  };

  // Handler for moving a group up or down
  const handleMoveGroup = (groupId: string, direction: 'up' | 'down') => {
    if (!quotation.flightGroups) return;
    
    const currentIndex = quotation.flightGroups.findIndex(g => g.id === groupId);
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' 
      ? Math.max(0, currentIndex - 1) 
      : Math.min(quotation.flightGroups.length - 1, currentIndex + 1);
    
    if (newIndex === currentIndex) return;
    
    const updatedGroups = [...quotation.flightGroups];
    const [movedGroup] = updatedGroups.splice(currentIndex, 1);
    updatedGroups.splice(newIndex, 0, movedGroup);
    
    // Update order property
    const groupsWithUpdatedOrder = updatedGroups.map((group, index) => ({
      ...group,
      order: index
    }));
    
    handleChange('flightGroups', groupsWithUpdatedOrder);
  };

  // Handler for adding flight to a group
  const handleAddFlightToGroup = (groupId: string, flightId: string) => {
    if (!quotation.flightGroups) return;
    
    const updatedGroups = quotation.flightGroups.map(group => {
      if (group.id === groupId) {
        // Only add if not already in group
        if (!group.flightIds.includes(flightId)) {
          return {
            ...group,
            flightIds: [...group.flightIds, flightId]
          };
        }
      }
      return group;
    });
    
    handleChange('flightGroups', updatedGroups);
  };

  // Handler for removing flight from a group
  const handleRemoveFlightFromGroup = (groupId: string, flightId: string) => {
    if (!quotation.flightGroups) return;
    
    const updatedGroups = quotation.flightGroups.map(group => {
      if (group.id === groupId) {
        return {
          ...group,
          flightIds: group.flightIds.filter(id => id !== flightId)
        };
      }
      return group;
    });
    
    handleChange('flightGroups', updatedGroups);
  };

  // Get flight information for display
  const getFlightInfo = (flight: ExtendedFlightInfo) => {
    return {
      title: flight.title || `${flight.departureAirport || ''} - ${flight.arrivalAirport || ''}`,
      subtitle: flight.airline ? `${flight.airline}${flight.flightNumber ? ` • ${flight.flightNumber}` : ''}` : 'Voo sem empresa'
    };
  };

  // Handler for drag and drop
  const handleDragEnd = (result: DropResult) => {
    const { source, destination, type } = result;
    
    // Dropped outside the list
    if (!destination) return;
    
    // No change needed
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;
    
    // Reordering groups
    if (type === 'group') {
      const reorderedGroups = [...groups];
      const [movedGroup] = reorderedGroups.splice(source.index, 1);
      reorderedGroups.splice(destination.index, 0, movedGroup);
      
      // Update order property
      const updatedGroups = reorderedGroups.map((group, index) => ({
        ...group,
        order: index
      }));
      
      handleChange('flightGroups', updatedGroups);
      return;
    }
    
    // Moving flights between groups
    if (type === 'flight') {
      const flightId = result.draggableId;
      const sourceGroupId = source.droppableId;
      const destGroupId = destination.droppableId;
      
      // If moving within the same group
      if (sourceGroupId === destGroupId) {
        // Currently we don't reorder flights within a group - could add this later
        return;
      }
      
      // Moving between groups
      const updatedGroups = groups.map(group => {
        // Remove from source group
        if (group.id === sourceGroupId) {
          return {
            ...group,
            flightIds: group.flightIds.filter(id => id !== flightId)
          };
        }
        
        // Add to destination group
        if (group.id === destGroupId) {
          return {
            ...group,
            flightIds: [...group.flightIds, flightId]
          };
        }
        
        return group;
      });
      
      handleChange('flightGroups', updatedGroups);
    }
  };

  // Render component
  return (
    <div className="space-y-6">
      <div className="flex flex-col space-y-1.5">
        <h3 className="text-lg font-semibold text-gray-800">Configuração de Voos</h3>
        <p className="text-sm text-gray-600">Personalize como os voos são agrupados e exibidos para o cliente.</p>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle>Descrição de Voos</CardTitle>
          <CardDescription>
            Esta descrição será exibida no início da aba de Voos para o cliente.
            Você pode adicionar observações gerais, instruções especiais ou detalhes importantes sobre os voos.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <RichTextEditor
            value={quotation.flightsDescription || ''}
            onChange={(value) => handleChange('flightsDescription', value)}
            readOnly={!isEditing}
            placeholder="Escreva uma descrição sobre os voos selecionados..."
            rows={6}
          />
          {isEditing && (
            <div className="mt-2 text-xs text-muted-foreground">
              Dica: Você pode adicionar formatação ao texto, incluir links, listas e outros elementos para melhorar a apresentação das informações ao cliente.
            </div>
          )}
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader>
          <CardTitle>Agrupamento de Voos</CardTitle>
          <CardDescription>
            Configure como os voos serão agrupados na visualização para o cliente.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-2 mb-4">
            <Switch
              id="use-custom-groups"
              checked={useCustomGroups}
              onCheckedChange={handleToggleCustomGroups}
            />
            <Label htmlFor="use-custom-groups">Usar agrupamento personalizado</Label>
            <span className="text-sm text-gray-500 ml-2">
              Quando ativado, você pode criar e organizar grupos de voos manualmente.
            </span>
          </div>
          
          {useCustomGroups ? (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-700">Grupos de Voos</h3>
                <Button
                  onClick={handleAddGroup}
                  variant="outline"
                  size="sm"
                  className="flex items-center"
                >
                  <PlusCircle className="h-4 w-4 mr-1" />
                  Adicionar Grupo
                </Button>
              </div>
              
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="flight-groups" type="group">
                  {(provided) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className="space-y-3"
                    >
                      {groups.map((group, index) => {
                        const groupFlights = group.flightIds
                          .map(id => allFlights.find(flight => flight.id === id))
                          .filter(Boolean) as ExtendedFlightInfo[];
                          
                        const unassignedFlights = allFlights.filter(
                          flight => !groups.some(g => g.flightIds.includes(flight.id))
                        );
                        
                        return (
                          <Draggable
                            key={group.id}
                            draggableId={group.id}
                            index={index}
                          >
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className="border rounded-md p-4 bg-gray-50"
                              >
                                {editingGroupId === group.id ? (
                                  <div className="w-full space-y-3">
                                    <div className="flex items-center mb-2">
                                      <div {...provided.dragHandleProps} className="mr-2 cursor-grab">
                                        <GripVertical className="h-5 w-5 text-gray-400" />
                                      </div>
                                      <Input
                                        value={newGroupName}
                                        onChange={(e) => setNewGroupName(e.target.value)}
                                        placeholder="Nome do grupo"
                                        className="text-base font-medium w-full"
                                      />
                                    </div>
                                    
                                    <RichTextEditor
                                      value={newGroupDescription}
                                      onChange={(value) => setNewGroupDescription(value)}
                                      placeholder="Descrição do grupo (opcional)"
                                      rows={3}
                                      className="w-full"
                                    />
                                    
                                    <div className="flex space-x-2">
                                      <Button
                                        size="sm"
                                        onClick={() => handleSaveGroupName(group.id)}
                                      >
                                        Salvar
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => setEditingGroupId(null)}
                                      >
                                        Cancelar
                                      </Button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex justify-between items-center mb-2">
                                    <div className="flex items-center">
                                      <div {...provided.dragHandleProps} className="mr-2 cursor-grab">
                                        <GripVertical className="h-5 w-5 text-gray-400" />
                                      </div>
                                      
                                      <div>
                                        <h4 className="text-base font-medium">{group.name}</h4>
                                        {group.description && (
                                          <RichTextEditor
                                            value={group.description}
                                            onChange={() => {}}
                                            readOnly={true}
                                            className="border-none shadow-none bg-transparent p-0 w-full text-sm text-gray-500"
                                          />
                                        )}
                                      </div>
                                    </div>
                                    
                                    <div className="flex items-center space-x-2">
                                      <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                          <Button variant="ghost" size="sm">
                                            <MoreHorizontal className="h-4 w-4" />
                                          </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                          <DropdownMenuItem onClick={() => handleEditGroupName(group.id, group.name, group.description)}>
                                            <Pencil className="h-4 w-4 mr-2" />
                                            Editar
                                          </DropdownMenuItem>
                                          <DropdownMenuItem onClick={() => handleMoveGroup(group.id, 'up')}>
                                            <ArrowUp className="h-4 w-4 mr-2" />
                                            Mover para cima
                                          </DropdownMenuItem>
                                          <DropdownMenuItem onClick={() => handleMoveGroup(group.id, 'down')}>
                                            <ArrowDown className="h-4 w-4 mr-2" />
                                            Mover para baixo
                                          </DropdownMenuItem>
                                          <DropdownMenuItem 
                                            onClick={() => handleRemoveGroup(group.id)}
                                            className="text-red-500 focus:text-red-500"
                                          >
                                            <Trash className="h-4 w-4 mr-2" />
                                            Remover grupo
                                          </DropdownMenuItem>
                                        </DropdownMenuContent>
                                      </DropdownMenu>
                                    </div>
                                  </div>
                                )}
                                
                                <div className="mt-4">
                                  <h5 className="text-sm font-medium mb-2">Voos neste grupo ({groupFlights.length})</h5>
                                  
                                  <Droppable droppableId={group.id} type="flight">
                                    {(provided) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                        className="min-h-[50px] bg-white border rounded-md p-2 space-y-2"
                                      >
                                        {groupFlights.length === 0 ? (
                                          <div className="text-center py-4 text-sm text-gray-500">
                                            Arraste voos para este grupo
                                          </div>
                                        ) : (
                                          <>
                                            {groupFlights.map((flight, flightIndex) => (
                                              <Draggable
                                                key={flight.id}
                                                draggableId={flight.id}
                                                index={flightIndex}
                                              >
                                                {(provided) => (
                                                  <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    className="flex justify-between items-center p-2 border rounded bg-gray-50 hover:bg-gray-100"
                                                  >
                                                    <div className="flex items-center">
                                                      <PlaneTakeoff className="h-4 w-4 mr-2 text-voah-teal" />
                                                      <div>
                                                        <div className="text-sm font-medium">
                                                          {getFlightInfo(flight).title}
                                                        </div>
                                                        <div className="text-xs text-gray-500">
                                                          {getFlightInfo(flight).subtitle}
                                                        </div>
                                                      </div>
                                                    </div>
                                                    <Button
                                                      variant="ghost"
                                                      size="sm"
                                                      onClick={() => handleRemoveFlightFromGroup(group.id, flight.id)}
                                                      className="h-6 w-6 p-0"
                                                    >
                                                      <X className="h-4 w-4" />
                                                    </Button>
                                                  </div>
                                                )}
                                              </Draggable>
                                            ))}
                                          </>
                                        )}
                                        {provided.placeholder}
                                      </div>
                                    )}
                                  </Droppable>
                                </div>
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
              
              {/* Unassigned flights section */}
              <div className="mt-6 border-t pt-4">
                <h3 className="text-lg font-medium text-gray-700 mb-3">Voos não agrupados</h3>
                
                <div className="bg-white border rounded-md p-3">
                  {allFlights.filter(
                    flight => !groups.some(g => g.flightIds.includes(flight.id))
                  ).length === 0 ? (
                    <div className="text-center py-4 text-sm text-gray-500">
                      Todos os voos estão agrupados
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {allFlights
                        .filter(flight => !groups.some(g => g.flightIds.includes(flight.id)))
                        .map(flight => (
                          <div
                            key={flight.id}
                            className="flex justify-between items-center p-2 border rounded bg-gray-50"
                          >
                            <div className="flex items-center">
                              <PlaneTakeoff className="h-4 w-4 mr-2 text-voah-teal" />
                              <div>
                                <div className="text-sm font-medium">
                                  {getFlightInfo(flight).title}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {getFlightInfo(flight).subtitle}
                                </div>
                                <div className="text-xs text-gray-400">
                                  Página: {flight.pageTitle || flight.pageId}
                                </div>
                              </div>
                            </div>
                            
                            <Select
                              onValueChange={(groupId) => handleAddFlightToGroup(groupId, flight.id)}
                            >
                              <SelectTrigger className="w-[140px] h-8 text-xs">
                                <SelectValue placeholder="Adicionar a..." />
                              </SelectTrigger>
                              <SelectContent>
                                {groups.map(group => (
                                  <SelectItem key={group.id} value={group.id} className="text-sm">
                                    {group.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 border rounded-md p-4 text-center">
              <p className="text-gray-600">
                Quando o agrupamento personalizado está desativado, os voos são agrupados automaticamente por página.
              </p>
              <Button
                onClick={() => handleToggleCustomGroups(true)}
                variant="outline"
                className="mt-3"
              >
                Ativar agrupamento personalizado
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
} 